import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, inCubic, outCubic, outExpo, phase, settle, span } from '../curves.ts';

/*
 * La Tenaza (models/corazon.ts, docs/sendas/artifice.md chapter 3): the slag crab. Idle (it breathes, its jaws twitch,
 * its eyes turn), walk (the legs in two alternate tripods, the body rocking side to side, the claws held up), `open`
 * (its warning, a loop: both claws raised and spread, the jaws wide open and shaking; three seconds of it), attack (the
 * snap: the claws thrown forward and the jaws slammed shut at 0.12 s, the body lunging), `grip` (a loop: the claws low
 * and forward round the wheel, the jaws clamped, the body straining back), hit, stun and die (it sinks, the legs
 * curl, the claws drop).
 *
 * Conventions: −X on a jaw opens it (its tip up); −X on an arm raises the claw, ±Y swings it out (+Y the left's
 * outward, −Y the right's); on a leg, ±Z lifts it (+Z the right side, −Z the left), Y swings it fore and aft; +X on a
 * knee folds the foot in. Legs 0–2 the right side front to back, 3–5 the left.
 */

type B = 'root' | 'body' | 'eyes' | 'armL' | 'armR' | 'clawL' | 'clawR' | 'jawL' | 'jawR' | 'leg0' | 'leg1' | 'leg2' | 'leg3' | 'leg4' | 'leg5' | 'knee0' | 'knee1' | 'knee2' | 'knee3' | 'knee4' | 'knee5';

const side = (i: number): number => (i < 3 ? 1 : -1);
const tripod = (i: number): number => (i === 0 || i === 2 || i === 4 ? 0 : 1);
const LEGS = [0, 1, 2, 3, 4, 5] as const;

/** One leg: `lift` off the ground, `stride` fore (+) or aft (−), `curl` folded in. */
const leg = (b: Bones<B>, i: number, lift: number, stride: number, curl = 0): void => {
  const s = side(i);
  b[`leg${i}` as B].rot(0, -s * stride * 0.3, s * (0.4 * lift + 0.5 * curl));
  b[`knee${i}` as B].rot(0.3 * lift + 1.1 * curl, 0, 0);
};

/** Both claws: `up` raised, `out` spread, `fwd` thrust forward; the jaws open by `open`. */
const claws = (b: Bones<B>, up: number, out: number, fwd: number, open: number): void => {
  b.armL.rot(-0.5 * up - 0.2 * fwd, 0.5 * out, 0);
  b.armR.rot(-0.5 * up - 0.2 * fwd, -0.5 * out, 0);
  b.clawL.rot(-0.2 * up + 0.3 * fwd, 0.2 * out, 0);
  b.clawR.rot(-0.2 * up + 0.3 * fwd, -0.2 * out, 0);
  b.jawL.rot(-0.75 * open, 0, 0);
  b.jawR.rot(-0.75 * open, 0, 0);
};

const idle = (): Clip<B> =>
  clip<B>('idle', 3.2, true, (b, t) => {
    const p = phase(t, 3.2) * TAU;
    b.body.pos(0, 0.012 * Math.sin(p), 0).rot(0.02 * Math.sin(p), 0, 0);
    b.eyes.rot(0, 0.25 * Math.sin(p * 2), 0);
    claws(b, 0.25 + 0.05 * Math.sin(p), 0.1, 0, 0.12 + 0.12 * Math.max(0, Math.sin(p * 3)));
    for (const i of LEGS) leg(b, i, 0, 0.1 * Math.sin(p + i));
  });

const walk = (): Clip<B> =>
  clip<B>('walk', 1.0, true, (b, t) => {
    const p = phase(t, 1.0);
    b.body.pos(0, 0.02 * Math.abs(Math.sin(p * TAU * 2)), 0).rot(0, 0, 0.05 * Math.sin(p * TAU));
    claws(b, 0.4, 0.15, 0, 0.15);
    b.eyes.rot(0, 0.1 * Math.sin(p * TAU), 0);
    for (const i of LEGS) {
      const k = p + tripod(i) * 0.5;
      const sw = Math.sin(k * TAU);
      leg(b, i, Math.max(0, sw), Math.cos(k * TAU));
    }
  });

/** Its warning: the claws up and spread, the jaws wide and shaking. */
const open = (): Clip<B> =>
  clip<B>('open', 0.6, true, (b, t) => {
    const p = phase(t, 0.6) * TAU;
    const sh = Math.sin(p * 4) * 0.05;
    b.body.rot(-0.12, 0, sh * 0.4).pos(0, 0.06, -0.04);
    b.eyes.rot(-0.2, 0, 0);
    claws(b, 1.4 + sh, 0.9, 0, 1.15 + sh * 2);
    for (const i of LEGS) leg(b, i, 0, -0.2, 0);
  });

/** The snap: thrown forward, shut at 0.12 s. */
const attack = (): Clip<B> =>
  clip<B>('attack', 0.9, false, (b, t) => {
    const lunge = span(t, 0, 0.12, outExpo) * (1 - span(t, 0.35, 0.9, outCubic));
    const shut = span(t, 0.08, 0.12, inCubic);
    b.body.rot(0.12 * lunge, 0, 0).pos(0, -0.03 * lunge, 0.12 * lunge + 0.03 * settle(t - 0.12, 4, 7));
    claws(b, 1.0 * (1 - lunge), 0.6 * (1 - lunge), 1.2 * lunge, 1.1 * (1 - shut));
    for (const i of LEGS) leg(b, i, 0, 0.25 * lunge);
  });

/** On the wheel: claws low and forward round it, clamped; straining back. */
const grip = (): Clip<B> =>
  clip<B>('grip', 0.8, true, (b, t) => {
    const p = phase(t, 0.8) * TAU;
    const strain = 0.03 * Math.sin(p * 2);
    b.body.rot(-0.08 + strain, 0, 0).pos(0, 0, -0.08 + strain);
    claws(b, -0.1, -0.15, 1.0, 0);
    for (const i of LEGS) leg(b, i, 0, -0.35 + strain * 2);
  });

const hit = (): Clip<B> =>
  clip<B>('hit', 0.4, false, (b, t) => {
    const k = settle(t, 3, 8);
    b.body.rot(-0.1 * k, 0, 0.06 * k).pos(0, 0.02 * k, -0.04 * k);
    claws(b, 0.5 + 0.3 * k, 0.3, 0, 0.3);
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 2, true, (b, t) => {
    const p = phase(t, 2) * TAU;
    b.body.rot(0.06, 0, 0.08 * Math.sin(p)).pos(0, -0.05, 0);
    claws(b, -0.2, 0.2, 0, 0.5);
    for (const i of LEGS) leg(b, i, 0, 0, 0.2);
  });

const die = (): Clip<B> =>
  clip<B>('die', 2.0, false, (b, t) => {
    const k = span(t, 0, 1.2, outCubic);
    b.body.pos(0, -0.22 * k, 0).rot(0.08 * k, 0, 0.12 * k);
    claws(b, -0.6 * k, 0.4 * k, 0, 0.8 * k);
    for (const i of LEGS) leg(b, i, 0, 0, k);
  });

export const tenazaClips = (): Clip[] => [idle(), walk(), open(), attack(), grip(), hit(), stun(), die()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { tenaza: tenazaClips };
